import { defineStore } from 'pinia'
import type { GuestProfileUpdate, GuestUser } from '~/types/guest'

export const useGuestStore = defineStore('guest', () => {
  const user = ref<GuestUser | null>(null)
  const isLoggedIn = computed(() => !!user.value)

  // Session load state for request deduplication (TR-40166): plugin and
  // middleware may request the session concurrently on the same navigation.
  let sessionLoaded = false
  let sessionPromise: Promise<void> | null = null

  // GA4 user id (TR-39953): push the pseudonymous tracking id after every
  // successful login / session restore, null after logout. Visitors that were
  // never logged in must not receive a user_id push at all.
  function pushUserIdToDataLayer (userId: string | null) {
    if (import.meta.server) {
      return
    }
    const win = window as typeof window & { dataLayer?: Record<string, unknown>[] }
    win.dataLayer = win.dataLayer || []
    win.dataLayer.push({ user_id: userId })
  }

  function setLoggedInUser (loggedInUser: GuestUser) {
    // Another account may have been logged in before in this app instance
    // (logout does not reload the page): cached account data must not leak
    // into the new session, otherwise /guest shows stale or no bookings
    // until a full reload.
    if (user.value?.email !== loggedInUser.email) {
      useGuestBookings().reset()
    }
    user.value = loggedInUser
    sessionLoaded = true
    if (loggedInUser.tracking_id) {
      pushUserIdToDataLayer(loggedInUser.tracking_id)
    }
  }

  async function loginWithGoogle (credential: string) {
    const response = await useNuxtApp().$backend.guest.loginWithGoogleFromClient(credential)
    setLoggedInUser(response.data)
  }

  // Email/password login or (with register=true) explicit sign-up. Returns:
  // - 'logged_in': user is logged in (user set).
  // - 'confirm_required': confirmation code required, a code is on its way.
  // - 'confirm_required_no_code': confirmation required, but NO code was sent
  //   (plain login, or the resend failed) - the UI must not promise a mail.
  async function loginWithCognito (email: string, password: string, register = false): Promise<'logged_in' | 'confirm_required' | 'confirm_required_no_code'> {
    const response = await useNuxtApp().$backend.guest.loginWithCognitoFromClient(email, password, register)
    const data = response.data
    if (data && 'status' in data && data.status === 'confirm_required') {
      return ('code_sent' in data && data.code_sent === false) ? 'confirm_required_no_code' : 'confirm_required'
    }
    setLoggedInUser(data as GuestUser)
    return 'logged_in'
  }

  // Confirm sign-up code and auto-login (same flow as Google). Returns
  // 'already_confirmed' when the account was confirmed before (no session).
  async function confirmSignUp (email: string, code: string): Promise<'logged_in' | 'already_confirmed'> {
    const response = await useNuxtApp().$backend.guest.confirmSignUpFromClient(email, code)
    const data = response.data
    if (data && 'status' in data && data.status === 'already_confirmed') {
      return 'already_confirmed'
    }
    setLoggedInUser(data as GuestUser)
    return 'logged_in'
  }

  async function resendSignUpConfirmationCode (email: string): Promise<'sent' | 'already_confirmed'> {
    const response = await useNuxtApp().$backend.guest.resendSignUpConfirmationCodeFromClient(email)
    const data = response.data
    if (data && 'status' in data && data.status === 'already_confirmed') {
      return 'already_confirmed'
    }
    return 'sent'
  }

  // Returns 'confirm_required' when the account exists but is not confirmed
  // yet: Cognito cannot send a reset code then, a confirmation code was sent
  // instead and the user must confirm first. 'confirm_required_no_code' means
  // the confirmation is required but the code resend failed - the UI must
  // point the user to the explicit "resend code" action instead.
  // 'google_only' means the guest has no Cognito password: do not show the
  // reset-code field, send them back to Google sign-in (F0 / TR-39733).
  async function sendPasswordResetCode (email: string): Promise<'sent' | 'confirm_required' | 'confirm_required_no_code' | 'google_only'> {
    const response = await useNuxtApp().$backend.guest.sendPasswordResetCodeFromClient(email)
    const data = response.data
    if (data && 'status' in data && data.status === 'confirm_required') {
      return ('code_sent' in data && data.code_sent === false) ? 'confirm_required_no_code' : 'confirm_required'
    }
    if (data && 'status' in data && data.status === 'google_only') {
      return 'google_only'
    }
    return 'sent'
  }

  async function confirmPasswordReset (email: string, code: string, newPassword: string) {
    await useNuxtApp().$backend.guest.confirmPasswordResetFromClient(email, code, newPassword)
  }

  async function logout () {
    await useNuxtApp().$backend.guest.logoutFromClient()
    user.value = null
    useGuestBookings().reset()
    pushUserIdToDataLayer(null)
  }

  // Loads the session exactly once per app instance: concurrent callers share
  // the same request, later callers get the cached result (TR-40166). Login
  // and logout keep the state consistent, so no invalidation is needed.
  function getSession (): Promise<void> {
    if (sessionLoaded) {
      return Promise.resolve()
    }
    if (!sessionPromise) {
      sessionPromise = (async () => {
        try {
          const response = await useNuxtApp().$backend.guest.getSessionFromClient()
          // 200 + empty data = logged out (homepage probe). Only a guest
          // with an email is a restored session.
          const guest = response.data
          if (guest && typeof guest === 'object' && 'email' in guest && guest.email) {
            setLoggedInUser(guest)
          } else {
            user.value = null
          }
        } catch {
          user.value = null
        } finally {
          sessionLoaded = true
          sessionPromise = null
        }
      })()
    }
    return sessionPromise
  }

  async function updateProfile (payload: GuestProfileUpdate) {
    const response = await useNuxtApp().$backend.guest.updateProfileFromClient(payload)
    user.value = response.data
  }

  async function deleteAccount (payload: { confirmation: string }) {
    await useNuxtApp().$backend.guest.deleteAccountFromClient(payload)
    user.value = null
    useGuestBookings().reset()
    pushUserIdToDataLayer(null)
  }

  // TR-40866: persist the newsletter prompt answer. Deliberately does NOT
  // clear the local pending flag: the prompt UI may show a confirmation hint
  // first and calls clearNewsletterPromptPending() when it is done (the flag
  // controls the v-if that keeps the prompt mounted).
  async function answerNewsletterPrompt (
    decision: 'subscribe' | 'dismiss',
    uiVariant?: 'modal' | 'one_tap' | 'returning',
  ) {
    await useNuxtApp().$backend.guest.answerNewsletterPromptFromClient(decision, uiVariant)
  }

  function clearNewsletterPromptPending () {
    if (user.value) {
      user.value = { ...user.value, newsletter_prompt_pending: false }
    }
  }

  return { user, isLoggedIn, loginWithGoogle, loginWithCognito, confirmSignUp, resendSignUpConfirmationCode, sendPasswordResetCode, confirmPasswordReset, logout, getSession, updateProfile, deleteAccount, answerNewsletterPrompt, clearNewsletterPromptPending }
})
